import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  Box,
  LinearProgress,
  IconButton
} from '@mui/material'
import {
  Close as CloseIcon,
  Storage as StorageIcon,
  LocationOn as LocationIcon,
  Thermostat as ThermostatIcon,
  WaterDrop as HumidityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material'
import { Humidor } from '../../store/api/humidorApi'
import { Cigar } from '../../store/api/cigarApi'
import HumidorContents from './HumidorContents'
import CigarDetailsDialog from '../cigars/CigarDetailsDialog'
import SessionForm from '../sessions/SessionForm'

interface HumidorDetailsProps {
  open: boolean
  onClose: () => void
  humidor: Humidor | null
  onEdit?: (humidor: Humidor) => void
  onDelete?: (humidor: Humidor) => void
}

const HumidorDetails: React.FC<HumidorDetailsProps> = ({
  open,
  onClose,
  humidor,
  onEdit,
  onDelete
}) => {
  const [viewingCigar, setViewingCigar] = React.useState<Cigar | null>(null)
  const [smokingCigar, setSmokingCigar] = React.useState<Cigar | null>(null)

  if (!humidor) return null

  const capacityPercentage = humidor.capacity > 0 ? (humidor.cigarCount / humidor.capacity) * 100 : 0

  const getCapacityColor = (percentage: number) => {
    if (percentage >= 90) return 'error'
    if (percentage >= 75) return 'warning'
    return 'success'
  }

  const getTemperatureColor = (current?: number, target?: number) => {
    if (!current || !target) return 'default'
    const diff = Math.abs(current - target)
    if (diff > 3) return 'error'
    if (diff > 1) return 'warning'
    return 'success'
  }

  const getHumidityColor = (current?: number, target?: number) => {
    if (!current || !target) return 'default'
    const diff = Math.abs(current - target)
    if (diff > 5) return 'error'
    if (diff > 2) return 'warning'
    return 'success'
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{humidor.name}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={3}>
          {/* Capacity Information */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <StorageIcon />
                  <Typography variant="h6">Capacity</Typography>
                </Box>
                
                <Typography variant="body1" gutterBottom>
                  <strong>{humidor.cigarCount}</strong> of <strong>{humidor.capacity}</strong> cigars
                </Typography>
                
                <Box mb={1}>
                  <LinearProgress 
                    variant="determinate" 
                    value={Math.min(capacityPercentage, 100)} 
                    color={getCapacityColor(capacityPercentage) as any}
                  />
                </Box>
                
                <Typography variant="body2" color="textSecondary">
                  {capacityPercentage.toFixed(1)}% full
                </Typography>
                
                {capacityPercentage >= 90 && (
                  <Chip 
                    label="Nearly Full" 
                    color="error" 
                    size="small" 
                    sx={{ mt: 1 }}
                  />
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Location Information */}
          {humidor.location && (
            <Grid item xs={12} sm={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <LocationIcon />
                    <Typography variant="h6">Location</Typography>
                  </Box>
                  <Typography variant="body1">{humidor.location}</Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Temperature Information */}
          <Grid item xs={12} sm={6}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <ThermostatIcon />
                  <Typography variant="h6">Temperature</Typography>
                </Box>
                
                {humidor.currentTemperature !== undefined ? (
                  <>
                    <Typography variant="body1">
                      Current: <strong>{humidor.currentTemperature}°F</strong>
                    </Typography>
                    {humidor.temperatureTarget && (
                      <>
                        <Typography variant="body2" color="textSecondary">
                          Target: {humidor.temperatureTarget}°F
                        </Typography>
                        <Chip 
                          label={`${Math.abs(humidor.currentTemperature - humidor.temperatureTarget)}°F difference`}
                          color={getTemperatureColor(humidor.currentTemperature, humidor.temperatureTarget) as any}
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      </>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No temperature data available
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Humidity Information */}
          <Grid item xs={12} sm={6}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <HumidityIcon />
                  <Typography variant="h6">Humidity</Typography>
                </Box>
                
                {humidor.currentHumidity !== undefined ? (
                  <>
                    <Typography variant="body1">
                      Current: <strong>{humidor.currentHumidity}%</strong>
                    </Typography>
                    {humidor.humidityTarget && (
                      <>
                        <Typography variant="body2" color="textSecondary">
                          Target: {humidor.humidityTarget}%
                        </Typography>
                        <Chip 
                          label={`${Math.abs(humidor.currentHumidity - humidor.humidityTarget)}% difference`}
                          color={getHumidityColor(humidor.currentHumidity, humidor.humidityTarget) as any}
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      </>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No humidity data available
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Target Conditions */}
          {(humidor.temperatureTarget || humidor.humidityTarget) && (
            <Grid item xs={12} sm={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Target Conditions</Typography>
                  {humidor.temperatureTarget && (
                    <Typography variant="body2">
                      Temperature: {humidor.temperatureTarget}°F
                    </Typography>
                  )}
                  {humidor.humidityTarget && (
                    <Typography variant="body2">
                      Humidity: {humidor.humidityTarget}%
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Notes */}
          {humidor.notes && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Notes</Typography>
                  <Typography variant="body2">{humidor.notes}</Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Metadata */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Details</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">
                      Created: {new Date(humidor.createdAt).toLocaleDateString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">
                      Last Updated: {new Date(humidor.updatedAt).toLocaleDateString()}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Cigar Contents */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Contents</Typography>
                <HumidorContents
                  humidorId={humidor.id}
                  onViewCigar={(cigar) => setViewingCigar(cigar)}
                  onStartSession={(cigar) => setSmokingCigar(cigar)}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {onEdit && (
          <Button 
            onClick={() => onEdit(humidor)} 
            startIcon={<EditIcon />}
            variant="outlined"
          >
            Edit
          </Button>
        )}
        {onDelete && humidor.cigarCount === 0 && (
          <Button 
            onClick={() => onDelete(humidor)} 
            startIcon={<DeleteIcon />}
            color="error"
            variant="outlined"
          >
            Delete
          </Button>
        )}
      </DialogActions>

      <CigarDetailsDialog
        open={!!viewingCigar}
        onClose={() => setViewingCigar(null)}
        cigar={viewingCigar}
        onStartSession={(cigar) => { setViewingCigar(null); setSmokingCigar(cigar) }}
      />

      <SessionForm
        open={!!smokingCigar}
        onClose={() => setSmokingCigar(null)}
        cigar={smokingCigar}
      />
    </Dialog>
  )
}

export default HumidorDetails
